/* Ask Sami on text selection.

   Select any prose on the page and a small "Ask Sami" chip appears by the
   selection; clicking it hands that text to Sami — a mounted Sami dock claims
   the suite's cancelable `semi360:copilot-ask` event, and with no dock the same
   text rides ?screen=sami to her workroom. No persistent button: the affordance
   exists only while there is a selection.

   Mount ONCE, high in the app tree (the shell). It manages a single fixed chip
   element on document.body and cleans up on unmount. Self-contained — it derives
   the hub host from the page's own hostname, so it needs no props or config. */
import { useEffect } from "react";

export default function AskSamiSelection() {
  useEffect(() => {
    if (typeof document === "undefined") return; // SSR guard
    if (window.__samiSelectMounted) return;
    window.__samiSelectMounted = true;

    const hubHost = () => {
      const host = window.location.hostname;
      if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host === "localhost")
        return "https://hub.dev.semi360.ai";
      const parts = host.split(".");
      const zone = parts.length > 2 ? parts.slice(1).join(".") : "semi360.ai";
      return window.location.protocol + "//hub." + zone;
    };

    const chip = document.createElement("button");
    chip.type = "button";
    chip.id = "samiSelChip";
    chip.setAttribute("aria-label", "Ask Sami about the selected text");
    chip.innerHTML =
      '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M8 10h8M8 14h5M21 12a9 9 0 1 1-3.6-7.2L21 3v6h-6"/></svg><span>Ask Sami</span>';
    chip.style.cssText =
      "position:absolute;z-index:2147483000;display:none;align-items:center;gap:5px;" +
      "padding:5px 10px;border-radius:100px;border:1px solid rgba(95,71,201,.35);" +
      "background:#5F47C9;color:#fff;font:600 12px/1 system-ui,-apple-system,'Segoe UI',sans-serif;" +
      "box-shadow:0 4px 14px rgba(20,15,50,.28);cursor:pointer;white-space:nowrap;user-select:none;" +
      "-webkit-user-select:none";
    // keep the selection alive when the chip is pressed
    const onChipDown = (e) => e.preventDefault();
    chip.addEventListener("mousedown", onChipDown);
    document.body.appendChild(chip);

    let lastText = "";
    const hide = () => { chip.style.display = "none"; lastText = ""; };

    const inField = (node) => {
      var n = node;
      while (n && n !== document.body) {
        if (n.nodeType === 1) {
          const t = (n.tagName || "").toLowerCase();
          if (t === "input" || t === "textarea" || t === "select") return true;
          if (n.isContentEditable) return true;
          if (n.id === "samiSelChip") return true;
        }
        n = n.parentNode;
      }
      return false;
    };

    const place = () => {
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) { hide(); return; }
      const text = String(sel.toString()).trim();
      if (text.length < 2) { hide(); return; }
      if (inField(sel.anchorNode) || inField(sel.focusNode)) { hide(); return; }
      lastText = text;
      const rects = sel.getRangeAt(0).getClientRects();
      const r = rects.length ? rects[rects.length - 1] : sel.getRangeAt(0).getBoundingClientRect();
      if (!r || (!r.width && !r.height)) { hide(); return; }
      chip.style.display = "inline-flex";
      const cw = chip.offsetWidth || 96, ch = chip.offsetHeight || 26;
      let left = window.scrollX + Math.min(r.right, window.innerWidth - cw - 8);
      left = Math.max(window.scrollX + 6, left);
      let top = window.scrollY + r.bottom + 6;
      if (r.bottom + ch + 10 > window.innerHeight) top = window.scrollY + r.top - ch - 6;
      chip.style.left = left + "px";
      chip.style.top = top + "px";
    };

    let raf = 0;
    const schedule = () => { if (raf) cancelAnimationFrame(raf); raf = requestAnimationFrame(place); };
    const onMouseUp = () => schedule();
    const onKeyUp = (e) => { if (e.shiftKey || e.key === "Shift") schedule(); };
    const onSelChange = () => {
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.isCollapsed) hide();
    };
    const onDocDown = (e) => { if (e.target !== chip && !chip.contains(e.target )) hide(); };
    const onScroll = () => hide();
    const onResize = () => hide();

    const onChipClick = (e) => {
      e.preventDefault(); e.stopPropagation();
      const text = lastText; if (!text) return;
      const command = "From this page: " + text;
      let claimed = false;
      try {
        const ev = new CustomEvent("semi360:copilot-ask", {
          detail: { command, selection: text }, bubbles: true, cancelable: true,
        });
        claimed = !document.dispatchEvent(ev);
      } catch (_e) { /* no CustomEvent — the workroom fallback still works */ }
      hide();
      try { var _s=window.getSelection(); if(_s) _s.removeAllRanges(); } catch (_e) {}
      if (!claimed) window.location.href = hubHost() + "/hub?screen=sami&q=" + encodeURIComponent(command);
    };

    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("selectionchange", onSelChange);
    document.addEventListener("mousedown", onDocDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    chip.addEventListener("click", onChipClick);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("selectionchange", onSelChange);
      document.removeEventListener("mousedown", onDocDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      chip.removeEventListener("click", onChipClick);
      chip.removeEventListener("mousedown", onChipDown);
      chip.remove();
      window.__samiSelectMounted = false;
    };
  }, []);

  return null;
}
